import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

/**
 * Thermal Receipt Component
 * Optimized for 58mm and 80mm thermal printers
 * Black & white only, monospace font, no emojis
 */
const ThermalReceipt = ({ 
  ticketData, 
  printerWidth = '80mm', // '58mm' or '80mm'
  onPrint,
  onClose 
}) => {
  const receiptRef = useRef(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  
  // Generate QR Code on mount
  useEffect(() => {
    const generateQR = async () => {
      try {
        const qrSize = printerWidth === '58mm' ? 120 : 150; // 30-35mm for 58mm, 35-45mm for 80mm
        const dataUrl = await QRCode.toDataURL('https://links.tiataxi.al', {
          width: qrSize,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'M'
        });
        setQrCodeDataUrl(dataUrl);
      } catch (err) {
        console.error('QR Code generation failed:', err);
      }
    };
    generateQR();
  }, [printerWidth]);

  // Format date as DD/MM/YYYY HH:mm
  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr || new Date());
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Generate invoice number: TIA-DDMMYYYY-NNNN
  const getInvoiceNumber = () => {
    if (ticketData?.ticket_number) return ticketData.ticket_number;
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const seq = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
    return `TIA-${day}${month}${year}-${seq}`;
  };

  // Handle print
  const handlePrint = () => {
    const printContent = receiptRef.current;
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>TIA Taxi Receipt</title>
        <style>
          ${getPrintStyles()}
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    onPrint?.();
  };

  // Print styles for thermal printer
  const getPrintStyles = () => `
    @page {
      size: ${printerWidth} auto;
      margin: 0;
    }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: ${printerWidth === '58mm' ? '10px' : '12px'};
      line-height: 1.3;
      color: #000;
      background: #fff;
      width: ${printerWidth};
      padding: ${printerWidth === '58mm' ? '2mm' : '3mm'};
    }
    
    .receipt {
      width: 100%;
    }
    
    .header {
      text-align: center;
      margin-bottom: 8px;
    }
    
    .logo {
      font-size: ${printerWidth === '58mm' ? '14px' : '18px'};
      font-weight: bold;
      letter-spacing: 1px;
    }
    
    .title {
      font-size: ${printerWidth === '58mm' ? '16px' : '20px'};
      font-weight: bold;
      margin-top: 4px;
    }
    
    .invoice-info {
      text-align: center;
      margin: 8px 0;
      font-size: ${printerWidth === '58mm' ? '9px' : '11px'};
    }
    
    .invoice-number {
      font-weight: bold;
      font-size: ${printerWidth === '58mm' ? '11px' : '13px'};
    }
    
    .divider {
      border: none;
      border-top: 1px dashed #000;
      margin: 8px 0;
    }
    
    .details {
      margin: 8px 0;
    }
    
    .detail-row {
      display: flex;
      justify-content: space-between;
      margin: 4px 0;
      font-size: ${printerWidth === '58mm' ? '10px' : '12px'};
    }
    
    .detail-label {
      text-align: left;
    }
    
    .detail-value {
      text-align: right;
      font-weight: bold;
    }
    
    .total-section {
      text-align: center;
      margin: 12px 0;
      padding: 8px 0;
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
    }
    
    .total-amount {
      font-size: ${printerWidth === '58mm' ? '24px' : '28px'};
      font-weight: bold;
      letter-spacing: 1px;
    }
    
    .total-secondary {
      font-size: ${printerWidth === '58mm' ? '12px' : '14px'};
      margin-top: 2px;
    }
    
    .payment-status {
      text-align: center;
      font-weight: bold;
      font-size: ${printerWidth === '58mm' ? '12px' : '14px'};
      margin: 8px 0;
      padding: 4px;
      border: 1px solid #000;
    }
    
    .qr-section {
      text-align: center;
      margin: 12px 0;
    }
    
    .qr-text {
      font-size: ${printerWidth === '58mm' ? '9px' : '10px'};
      margin-bottom: 4px;
    }
    
    .qr-code {
      width: ${printerWidth === '58mm' ? '30mm' : '40mm'};
      height: auto;
    }
    
    .qr-url {
      font-size: ${printerWidth === '58mm' ? '8px' : '9px'};
      margin-top: 4px;
    }
    
    .footer {
      text-align: center;
      margin-top: 12px;
      font-size: ${printerWidth === '58mm' ? '8px' : '10px'};
    }
    
    .footer-line {
      margin: 2px 0;
    }
    
    .thank-you {
      font-weight: bold;
      font-size: ${printerWidth === '58mm' ? '10px' : '12px'};
      margin-bottom: 4px;
    }
  `;

  const invoiceNumber = getInvoiceNumber();
  const dateTime = formatDateTime(ticketData?.created_at);
  const priceEur = ticketData?.price_eur || 0;
  const priceLek = ticketData?.price_lek || (priceEur * 100);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Receipt Preview */}
        <div className="p-4 bg-gray-50">
          <div 
            ref={receiptRef}
            className="bg-white mx-auto shadow-lg"
            style={{ 
              width: '280px',
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: '12px',
              padding: '12px',
              lineHeight: 1.3,
              color: '#000'
            }}
          >
            {/* HEADER */}
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 'bold',
                letterSpacing: '1px'
              }}>
                TIA AIRPORT TAXI
              </div>
              <div style={{ 
                fontSize: '9px',
                color: '#666',
                marginTop: '2px'
              }}>
                NIPT: L12345678A
              </div>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: 'bold',
                marginTop: '4px'
              }}>
                TAXI TICKET
              </div>
            </div>

            {/* INVOICE INFO */}
            <div style={{ textAlign: 'center', margin: '8px 0' }}>
              <div style={{ 
                fontWeight: 'bold',
                fontSize: '13px'
              }}>
                {invoiceNumber}
              </div>
              <div style={{ fontSize: '11px' }}>
                {dateTime}
              </div>
            </div>

            {/* DIVIDER */}
            <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '8px 0' }} />

            {/* IMPORTANT INFO - TAXI & DRIVER */}
            <div style={{ 
              margin: '10px 0', 
              padding: '8px', 
              border: '2px solid #000',
              backgroundColor: '#f5f5f5'
            }}>
              {/* Taxi ID */}
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '4px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                TAXI: {ticketData?.taxi_id || 'N/A'}
              </div>
              
              {/* License Plate */}
              <div style={{ 
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                letterSpacing: '1px',
                marginBottom: '4px'
              }}>
                TARGA: {ticketData?.vehicle_plate || 'N/A'}
              </div>

              {/* Driver Name */}
              {ticketData?.driver_name && (
                <div style={{ 
                  textAlign: 'center',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  Driver/Shoferi: <strong>{ticketData.driver_name}</strong>
                </div>
              )}
            </div>

            {/* Passenger Name if provided */}
            {ticketData?.passenger_name && (
              <div style={{ 
                textAlign: 'center', 
                margin: '6px 0',
                fontSize: '11px'
              }}>
                Passenger/Pasagjeri: <strong>{ticketData.passenger_name}</strong>
              </div>
            )}

            {/* RIDE DETAILS */}
            <div style={{ margin: '8px 0' }}>
              {/* Destination */}
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                <span>Destination/Destinacioni:</span>
                <span style={{ fontWeight: 'bold', textAlign: 'right', maxWidth: '50%' }}>
                  {ticketData?.destination || 'Tirana Center'}
                </span>
              </div>

              {/* Passengers */}
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                <span>Passengers/Pasagjere:</span>
                <span style={{ fontWeight: 'bold' }}>{ticketData?.passenger_count || 1}</span>
              </div>

              {/* Vehicle Type */}
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
                <span>Vehicle/Automjeti:</span>
                <span style={{ fontWeight: 'bold' }}>
                  {ticketData?.vehicle_type === 'van' ? 'VAN' : 'SEDAN'}
                </span>
              </div>
            </div>

            {/* DIVIDER */}
            <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '8px 0' }} />

            {/* TOTAL PRICE - Left/Right aligned */}
            <div style={{ 
              margin: '10px 0',
              padding: '8px 0',
              borderTop: '1px solid #000',
              borderBottom: '1px solid #000'
            }}>
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                <span>TOTAL:</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '16px' }}>
                    {priceLek.toLocaleString()}.00 L
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#444' }}>
                    = € {priceEur.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* PAYMENT STATUS - Bold bilingual */}
            <div style={{ 
              textAlign: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              margin: '8px 0',
              padding: '6px',
              border: '1px dashed #000'
            }}>
              To be paid at destination / Paguhet në destinacion
            </div>

            {/* DIVIDER */}
            <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '8px 0' }} />

            {/* QR CODE SECTION */}
            <div style={{ textAlign: 'center', margin: '12px 0' }}>
              <div style={{ fontSize: '10px', marginBottom: '4px' }}>
                Download our app / Shkarko aplikacionin
              </div>
              {qrCodeDataUrl && (
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Code"
                  style={{ 
                    width: '120px',
                    height: 'auto',
                    margin: '0 auto'
                  }}
                />
              )}
              <div style={{ fontSize: '9px', marginTop: '4px' }}>
                https://links.tiataxi.al
              </div>
            </div>

            {/* DIVIDER */}
            <hr style={{ border: 'none', borderTop: '1px dashed #000', margin: '8px 0' }} />

            {/* FOOTER */}
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '10px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '4px' }}>
                Thank you! / Faleminderit!
              </div>
              <div style={{ margin: '4px 0' }}>
                +355 69 999 9300 | support@tiataxi.al
              </div>
            </div>

            {/* Bottom spacing for paper cut */}
            <div style={{ height: '20px' }}></div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white p-4 border-t flex gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 bg-black hover:bg-gray-800 text-white font-bold py-3 px-4 rounded-lg transition"
          >
            PRINTO
          </button>
          <button
            onClick={onClose}
            className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg transition"
          >
            Mbyll
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThermalReceipt;
